// ============================================================================
// ClientWaterTracker — quick-add water + today's total vs target (Wave 2).
// ----------------------------------------------------------------------------
// Two big tap targets (+250 ml / +500 ml) POST an increment to /me/water (the
// backend upserts the day's single row), then the today total live-updates from
// the invalidated water query. The target comes from the nutrition-targets
// payload (water_ml); when absent we show the raw total only. Mobile-first,
// big touch targets, Pulse ocean accent, EN + 繁中.
// ============================================================================
import { useTranslation } from "react-i18next";
import { Banner, Card, CardHeader } from "../../components";
import {
  useAddClientWater,
  useClientWater,
  useNutritionTargets,
  todayDay,
} from "../hooks";
import { fmtNum } from "./portalLib";

function WaterDropIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" aria-hidden>
      <path
        d="M12 3.5c3.6 4.2 6 7.4 6 10.5a6 6 0 0 1-12 0c0-3.1 2.4-6.3 6-10.5z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function ClientWaterTracker() {
  const { t } = useTranslation("nutrition");
  const water = useClientWater();
  const targets = useNutritionTargets();
  const add = useAddClientWater();

  const today = todayDay();
  const total = water.data?.find((w) => w.day === today)?.ml ?? 0;
  const target = targets.data?.water_ml ?? null;
  const pct =
    target && target > 0 ? Math.min(100, Math.round((total / target) * 100)) : 0;

  const quickAdd = (deltaMl: number) =>
    add.mutate({ day: today, delta_ml: deltaMl });

  return (
    <Card className="space-y-4">
      <CardHeader title={t("water.title")} subtitle={t("water.subtitle")} />

      <div className="rounded-lg bg-surface-sunken px-4 py-4 text-center">
        <div className="flex items-center justify-center gap-2 text-ocean">
          <WaterDropIcon />
          <span className="font-display text-3xl font-extrabold tracking-tightest tabular-nums text-body">
            {fmtNum(total, 0)}
          </span>
          <span className="font-display text-base font-bold text-body-soft">
            {t("water.mlUnit")}
          </span>
        </div>
        <p className="mt-1 text-sm text-body-soft">
          {target != null ? t("water.ofTarget", { target: fmtNum(target, 0) }) : t("water.noTarget")}
        </p>
        {target != null && (
          <div
            className="mt-3 h-2.5 w-full overflow-hidden rounded-pill bg-surface"
            role="progressbar"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={t("water.title")}
          >
            <div
              className="h-full rounded-pill bg-grad-ocean transition-[width] duration-500 ease-kinetic"
              style={{ width: `${pct}%` }}
            />
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => quickAdd(250)}
          disabled={add.isPending}
          className="flex items-center justify-center gap-2 rounded-lg bg-ocean-soft px-4 py-4 font-display text-base font-bold text-ocean transition-colors hover:bg-ocean/15 disabled:opacity-60"
        >
          <WaterDropIcon />
          {t("water.addCup")}
        </button>
        <button
          type="button"
          onClick={() => quickAdd(500)}
          disabled={add.isPending}
          className="flex items-center justify-center gap-2 rounded-lg bg-ocean-soft px-4 py-4 font-display text-base font-bold text-ocean transition-colors hover:bg-ocean/15 disabled:opacity-60"
        >
          <WaterDropIcon />
          {t("water.addGlass")}
        </button>
      </div>

      {add.isError && (
        <Banner tone="danger" title={t("saveError")}>
          {t("saveErrorBlurb")}
        </Banner>
      )}
    </Card>
  );
}
